import { useState } from "react";
import { Play, ExternalLink } from "lucide-react";
import { Reveal, Overline } from "./Reveal";

const VIDEOS = [
  { id: "Z4sIZUK8lIM", stat: "$88K", label: "Amazon store", tag: "March 2025" },
  { id: "s-2MxZtGSnQ", stat: "$1M", label: "month chase", tag: "March 2025" },
  { id: "vVoHzQA_zew", stat: "$300K", label: "store case study", tag: "March 2025" },
  { id: "upMw9S7_ZIo", stat: "$150K", label: "payout", tag: "February 2025" },
  { id: "4gpjknJDLRU", stat: "$100K", label: "payout store", tag: "Client store" },
  { id: "tfVK0QuTaKI", stat: "$8,500", label: "in sales per day", tag: "Success story" },
];

const VAULT = [
  { id: "1BRjr92yAI5qKMVJs6Y34MK3eoy5plBHZ", name: "May Case Study 11" },
  { id: "1K2FSgDq9JaNkkqLK0SCohLrmH4CGO8kx", name: "May Case Study 12" },
];

function VideoFacade({ video, index }) {
  const [playing, setPlaying] = useState(false);
  return (
    <Reveal delay={(index % 3) * 0.1} className="bg-[#F4F2EC]">
      <article data-testid={`video-case-${index}`} className="group h-full">
        <div className="relative aspect-video overflow-hidden bg-[#F4F2EC]">
          {playing ? (
            <iframe
              src={`https://www.youtube-nocookie.com/embed/${video.id}?autoplay=1&rel=0`}
              title={`DSA eCommerce case study — ${video.stat} ${video.label}`}
              className="h-full w-full"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
            />
          ) : (
            <button
              data-testid={`video-play-${index}`}
              onClick={() => setPlaying(true)}
              aria-label={`Play case study: ${video.stat} ${video.label}`}
              className="relative block h-full w-full"
            >
              <img
                src={`https://i.ytimg.com/vi/${video.id}/hqdefault.jpg`}
                alt=""
                loading="lazy"
                className="h-full w-full object-cover saturate-[0.35] transition-transform duration-700 ease-out group-hover:scale-105"
              />
              <span className="absolute inset-0 bg-black/40 transition-colors duration-300 group-hover:bg-black/20" />
              <span className="absolute left-1/2 top-1/2 flex h-14 w-14 -translate-x-1/2 -translate-y-1/2 items-center justify-center bg-signal text-white transition-colors duration-200 group-hover:bg-ink group-hover:text-white">
                <Play className="ml-0.5 h-5 w-5 fill-current" />
              </span>
              <span className="absolute left-4 top-4 bg-black/70 px-3 py-1.5 font-mono2 text-[10px] uppercase tracking-[0.2em] text-[#FFD60A] backdrop-blur">
                {video.tag}
              </span>
            </button>
          )}
        </div>
        <div className="flex items-baseline gap-3 p-6 md:p-8">
          <span className="font-display text-4xl font-black tracking-tighter md:text-5xl">
            {video.stat}
          </span>
          <span className="font-mono2 text-[11px] uppercase tracking-[0.2em] text-signal">
            {video.label}
          </span>
        </div>
      </article>
    </Reveal>
  );
}

export function VideoCaseStudies() {
  return (
    <section data-testid="video-case-studies-section" className="border-t border-black/10 bg-white">
      <div className="mx-auto max-w-[1600px] px-5 py-32 md:px-10 md:py-48">
        <Reveal className="flex flex-wrap items-end justify-between gap-6">
          <div>
            <Overline>Straight from our channel</Overline>
            <h2 className="mt-6 max-w-3xl font-display text-4xl font-black tracking-tighter sm:text-5xl lg:text-6xl">
              Don't take our word for it.{" "}
              <span className="text-ink/40">Watch the stores.</span>
            </h2>
          </div>
          <a
            href="https://www.youtube.com/@DSAeCommerce"
            target="_blank"
            rel="noopener noreferrer"
            data-testid="youtube-channel-link"
            className="group inline-flex items-center gap-2 border border-black/20 px-5 py-3 font-mono2 text-[11px] uppercase tracking-[0.2em] text-ink/70 transition-colors duration-200 hover:border-ink hover:text-ink"
          >
            More on YouTube
            <ExternalLink className="h-3.5 w-3.5 transition-transform duration-200 group-hover:-translate-y-0.5 group-hover:translate-x-0.5" />
          </a>
        </Reveal>

        <div className="mt-20 grid gap-px bg-black/10 md:grid-cols-2 lg:grid-cols-3">
          {VIDEOS.map((v, i) => (
            <VideoFacade key={v.id} video={v} index={i} />
          ))}
        </div>

        <Reveal delay={0.15} className="mt-20">
          <div className="flex flex-wrap items-center justify-between gap-6 border-t border-black/10 pt-12">
            <div>
              <p className="font-mono2 text-xs uppercase tracking-[0.25em] text-signal">
                The private vault
              </p>
              <h3 className="mt-4 font-display text-2xl font-bold tracking-tight md:text-3xl">
                Raw client case studies — straight from our Drive.
              </h3>
            </div>
            <a
              href="https://drive.google.com/drive/folders/1DhRGnQX96iTpLrXJSKOGPHWJFUeODUTA"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="drive-vault-link"
              className="group inline-flex items-center gap-2 border border-black/20 px-5 py-3 font-mono2 text-[11px] uppercase tracking-[0.2em] text-ink/70 transition-colors duration-200 hover:border-signal hover:text-signal"
            >
              Open all 9 case studies
              <ExternalLink className="h-3.5 w-3.5 transition-transform duration-200 group-hover:-translate-y-0.5 group-hover:translate-x-0.5" />
            </a>
          </div>
          <div className="mt-10 grid gap-px bg-black/10 md:grid-cols-2">
            {VAULT.map((v, i) => (
              <div key={v.id} data-testid={`vault-video-${i}`} className="bg-[#F4F2EC]">
                <div className="aspect-video">
                  <iframe
                    src={`https://drive.google.com/file/d/${v.id}/preview`}
                    title={v.name}
                    loading="lazy"
                    className="h-full w-full"
                    allow="autoplay"
                    allowFullScreen
                  />
                </div>
                <p className="p-5 font-mono2 text-[10px] uppercase tracking-[0.2em] text-ink/50">
                  {v.name}
                </p>
              </div>
            ))}
          </div>
          <p className="mt-8 font-body text-xs text-ink/35">
            Individual results shown are not typical; your results will vary.
          </p>
        </Reveal>
      </div>
    </section>
  );
}
